import { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { Alert, Button, CircularProgress, Link, Stack, Typography } from '@mui/material';
import { ApiError, confirmVerificationRequest } from '@/auth/api';
import { useAuth } from '@/auth/useAuth';
import { NarrowPage } from '@/components/NarrowPage';
import { PageHeader } from '@/components/PageHeader';
import { SectionCard } from '@/components/SectionCard';
import { takePendingReservation } from '@/features/account/reservations';

type VerificationState = 'loading' | 'success' | 'invalid' | 'unavailable';

export function CustomerVerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const { refreshUser, token } = useAuth();
  const [state, setState] = useState<VerificationState>('loading');
  /// The device the buyer wanted to reserve before verifying (VEN-3), to go back to it.
  const [pendingDeviceId, setPendingDeviceId] = useState<string | null>(null);
  const started = useRef(false);
  const verificationToken = searchParams.get('token');

  const verify = useCallback(() => {
    if (!verificationToken) {
      setState('invalid');
      return;
    }

    setState('loading');
    void confirmVerificationRequest(verificationToken)
      .then(async () => {
        if (token) await refreshUser().catch(() => null);
        setPendingDeviceId(takePendingReservation());
        setState('success');
      })
      .catch((error: unknown) => {
        // The backend answers 400 for a link that is unknown, used or expired. Anything else
        // (network, server, rate limit) leaves the link untouched, so the buyer can retry it.
        const linkRejected =
          error instanceof ApiError &&
          error.status >= 400 &&
          error.status < 500 &&
          error.status !== 429;
        setState(linkRejected ? 'invalid' : 'unavailable');
      });
  }, [refreshUser, token, verificationToken]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    verify();
  }, [verify]);

  return (
    <NarrowPage maxWidth={600}>
      <PageHeader
        eyebrow="VERIFICACIÓN DE EMAIL"
        title={state === 'success' ? 'Email verificado' : 'Verificá tu email'}
        description="La verificación protege tu cuenta y será necesaria para reservar o comprar."
      />
      <SectionCard title="Estado de la verificación">
        {state === 'loading' ? (
          <Stack direction="row" spacing={2} alignItems="center" role="status">
            <CircularProgress size={24} />
            <Typography>Estamos validando tu enlace...</Typography>
          </Stack>
        ) : state === 'success' && pendingDeviceId ? (
          <Alert severity="success">
            Tu cuenta quedó verificada.{' '}
            <Link
              component={RouterLink}
              to={token ? `/catalog/${pendingDeviceId}?reserve=1` : `/catalog/${pendingDeviceId}`}
              underline="always"
            >
              Volver al equipo para reservarlo
            </Link>
            .
          </Alert>
        ) : state === 'success' ? (
          <Alert severity="success">
            Tu cuenta quedó verificada. Ya podés{' '}
            <Link component={RouterLink} to="/login" underline="always">
              ingresar
            </Link>{' '}
            o volver al{' '}
            <Link component={RouterLink} to="/catalog">
              catálogo
            </Link>
            .
          </Alert>
        ) : state === 'unavailable' ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={verify}>
                Reintentar
              </Button>
            }
          >
            No pudimos verificar tu enlace en este momento. Volvé a intentar en unos minutos.
          </Alert>
        ) : (
          <Alert severity="error">
            El enlace no es válido o venció. Podés{' '}
            <Link component={RouterLink} to="/account/check-email" underline="always">
              solicitar uno nuevo
            </Link>
            .
          </Alert>
        )}
      </SectionCard>
    </NarrowPage>
  );
}
